// Touched to force a cold rebuild of this app on CI (memory-cap check).
import { AuthProvider } from "@babackapp/auth-client/context";
import { InformationBanner } from "@babackapp/ui/components/information-banner";
import "@babackapp/auth-client/setup";
import { Toaster } from "@babackapp/ui/components/sonner";
import { TooltipProvider } from "@babackapp/ui/components/tooltip";
import "@babackapp/ui/globals.css";
import { datadogRum } from "@datadog/browser-rum";
import {
  asyncWithLDProvider,
  type LDOptions,
} from "launchdarkly-react-client-sdk";
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { HomeLoading } from "./components/home-loading";
import config from "./config.ts";
import "./index.css";
import "./lib/i18n/i18n";

import QueryClientProvider from "./providers/query-client-provider.tsx";
import Router from "./router.tsx";

// Initialize Datadog RUM before React
if (
  config.datadog?.enabled &&
  config.datadog?.applicationId &&
  config.datadog?.clientToken
) {
  datadogRum.init({
    applicationId: config.datadog.applicationId,
    clientToken: config.datadog.clientToken,
    site: config.datadog.site || "datadoghq.eu",
    service: config.datadog.service || "baback-office",
    env: config.datadog.env || (config.isProd ? "production" : "development"),
    version: config.datadog.version,
    sessionSampleRate: config.datadog.sessionSampleRate || 100,
    sessionReplaySampleRate: config.datadog.sessionReplaySampleRate || 20,
    defaultPrivacyLevel:
      config.datadog.defaultPrivacyLevel || "mask-user-input",
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    allowedTracingUrls: [
      config.api.url,
      ...(config.api.workerUrl ? [config.api.workerUrl] : []),
    ],
  });
}

(async () => {
  let options: LDOptions = {};
  if (config.launchDarkly.devMode) {
    options = {
      streaming: false,
      bootstrap: "localStorage",
      sendEvents: false,
    };
  }
  const LDProvider = await asyncWithLDProvider({
    clientSideID: config.launchDarkly.clientSideId,
    context: {
      kind: "anonymous",
      key: "anonymous-user",
      anonymous: true,
    },
    options,
  });

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <LDProvider>
        <App />
      </LDProvider>
    </React.StrictMode>,
  );
})();

export default function App() {
  return (
    <>
      <InformationBanner />
      <QueryClientProvider>
        <AuthProvider loginRoute="/auth/sign-in" excludeRedirectPaths={["/return-widget"]}>
          <Suspense fallback={<HomeLoading />}>
            <TooltipProvider>
              <Router />
              <Toaster position="top-right" />
            </TooltipProvider>
          </Suspense>
        </AuthProvider>
      </QueryClientProvider>
    </>
  );
}
